import { useState, useEffect } from "react";
import AppSelect from "../ui/AppSelect";
import debounce from "lodash.debounce";
import { apiClient } from "../../apiClient";
import { Contatto } from "../../types/index";
import linkedin from "../../assets/linkedin.svg";
import cuoreVuoto from "../../assets/heart-empty.svg";
import cuorePieno from "../../assets/heart-full.svg";
import cestino from "../../assets/delete.svg";
import info from "../../assets/info.svg";
import { useAuth } from '../../context/AuthContext';
import DettagliContattoModal from "../ui/Leads/DettagliContattoModal";
import { LeadCard, PanelFilterBar, PanelHero } from "../ui/panel";

interface Option {
  value: string;
  label: string;
}

type TipoContatto = "contatto" | "small-lead" | "startup";

export default function PreferitiView() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [contatti, setContatti] = useState<Contatto[]>([]);
  const [filters, setFilters] = useState({ nome: "", associato: "", servizio: "", esito: "", tipo: "" });
  const [nomeInput, setNomeInput] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contatto | null>(null);
  
  // Opzioni dei filtri caricate dal backend per prevenire l'errore TS6133
  const [options, setOptions] = useState<{ associati: Option[], servizi: Option[], esiti: Option[] }>({
    associati: [], servizi: [], esiti: []
  });

  const tipoOptions: Option[] = [
    { value: "", label: "Tutti" },
    { value: "contatto", label: "Linkedin" },
    { value: "small-lead", label: "Small Leads" },
    { value: "startup", label: "Startup Toscane" },
  ];

  const debouncedSetNome = debounce((value: string) => {
    setFilters((prev) => ({ ...prev, nome: value }));
  }, 300);

  const fetchOptions = async () => {
    try {
      const [associati, servizi, esiti] = await Promise.all([
        apiClient.get("json/associati"),
        apiClient.get("json/servizi"),
        apiClient.get("json/esiti"),
      ]);
      setOptions({ associati: associati.data, servizi: servizi.data, esiti: esiti.data });
    } catch (err) {
      console.error("Errore caricamento opzioni:", err);
    }
  };

  // === FETCH PREFERITI ===
  const fetchPreferiti = async (filt = filters) => {
    try {
      const res = await apiClient.get("contatti/preferiti", {
        params: {
          nome: filt.nome || undefined,
          associato: isAdmin ? (filt.associato || undefined) : undefined,
          servizio: filt.servizio || undefined,
          esito: filt.esito || undefined,
          tipo: filt.tipo || undefined,
        },
      });

      setContatti(
        (res.data.contatti || []).map((c: Contatto) => ({
          ...c,
          preferito: c.preferito ?? true,
          numero_contatti: c.numero_contatti ?? 0,
        }))
      );
    } catch (err) {
      console.error("Errore caricamento preferiti:", err);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchPreferiti();
  }, []);

  useEffect(() => {
    fetchPreferiti();
  }, [filters]);

  const togglePreferito = async (id: number, attuale: boolean, tipo: TipoContatto) => {
    try {
      setContatti((prev) =>
        prev.map((c) => (c.id === id && c.tipo === tipo ? { ...c, preferito: !attuale } : c))
      );
      await apiClient.put(
        `contatti/${id}/preferito?tipo=${tipo}`,
        { preferito: !attuale }
      );
    } catch (err) {
      console.error("Errore toggle preferito:", err);
    }
  };

  const eliminaContatto = async (id: number, tipo: TipoContatto) => {
    try {
      await apiClient.delete(`deleteContatto/${id}?tipo=${tipo}`);
      setContatti((prev) => prev.filter((c) => !(c.id === id && c.tipo === tipo)));
      setDeleteConfirmId(null);
    } catch (err) {
      console.error("Errore eliminazione:", err);
    }
  };

  const handleContactSaved = (updated: Contatto) => {
    setContatti((prev) =>
      prev.map((item) => (item.id === updated.id && item.tipo === updated.tipo ? { ...item, ...updated } : item))
    );
    setSelectedContact(updated);
  };

  const contattiFiltrati = contatti
    .filter((c) => c.preferito)
    .filter((c) => {
      const fullName = `${c.nome || ""} ${c.cognome || ""} ${c.azienda || ""}`.toLowerCase();
      return (
        fullName.includes(filters.nome.toLowerCase()) &&
        (isAdmin && filters.associato ? c.associato === filters.associato : true) &&
        (filters.servizio ? c.servizio_proposto === filters.servizio : true) &&
        (filters.esito ? c.esito_contatto === filters.esito : true) &&
        (filters.tipo ? c.tipo === filters.tipo : true)
      );
    });

  return (
    <div className="space-y-6 pb-10">
      <PanelHero
        variant="brand"
        eyebrow="CRM"
        title="Preferiti"
        description="I lead che hai contrassegnato come prioritari."
      />

      <PanelFilterBar>
        <input
          type="text"
          placeholder="Cerca per nome o azienda..."
          className="min-w-[220px] flex-1 rounded-xl border border-secondary/15 bg-secondary/[0.02] px-3 py-2.5 text-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/15"
          value={nomeInput}
          onChange={(e) => {
            const value = e.target.value.trimStart();
            setNomeInput(value);
            debouncedSetNome(value);
          }}
        />
        {isAdmin && (
          <div className="w-full sm:w-44">
            <AppSelect
              options={options.associati}
              placeholder="Associato"
              isClearable
              onChange={(o) => setFilters((p) => ({ ...p, associato: o?.value ?? "" }))}
              className="w-full sm:w-44"
            />
          </div>
        )}
        <div className="w-full sm:w-44">
          <AppSelect options={options.servizi} placeholder="Servizio" isClearable onChange={(o) => setFilters((p) => ({ ...p, servizio: o?.value ?? "" }))} className="w-full sm:w-44" />
        </div>
        <div className="w-full sm:w-44">
          <AppSelect options={options.esiti} placeholder="Esito" isClearable onChange={(o) => setFilters((p) => ({ ...p, esito: o?.value ?? "" }))} className="w-full sm:w-44" />
        </div>
        <div className="w-full sm:w-44">
          <AppSelect options={tipoOptions} placeholder="Tipo Lead" isClearable onChange={(o) => setFilters((p) => ({ ...p, tipo: o?.value ?? "" }))} className="w-full sm:w-44" />
        </div>
      </PanelFilterBar>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {contattiFiltrati.map((c) => {
          const badgeStyles = {
            contatto: "bg-blue-50 text-blue-700 border-blue-100",
            "small-lead": "bg-amber-50 text-amber-700 border-amber-100",
            startup: "bg-purple-50 text-purple-700 border-purple-100"
          }[c.tipo];

          const label = { contatto: "Linkedin", "small-lead": "Small Lead", startup: "Startup" }[c.tipo];
          const referenteStartup = `${c.nome || ""} ${c.cognome || ""}`.trim();
          const nomeCard = c.tipo === "startup" ? (c.azienda || "Azienda non disponibile") : `${c.nome} ${c.cognome || ""}`;

          return (
            <LeadCard key={`${c.tipo}-${c.id}`} className="min-h-[160px]">
              <div>
                <div className="flex justify-between items-start mb-3">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full border ${badgeStyles}`}>
                    {label}
                  </span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                     {c.linkedin_url && (
                        <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="hover:scale-110 transition">
                          <img src={linkedin} className="w-4 h-4" alt="LinkedIn" />
                        </a>
                     )}
                  </div>
                </div>
                <h3 className="mb-1 truncate text-lg font-extrabold leading-snug text-secondary" title={nomeCard}>
                  {nomeCard}
                </h3>
                {c.tipo === "startup" && referenteStartup && (
                  <p className="text-xs text-gray-400 italic">Referente: {referenteStartup}</p>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                <button 
                  onClick={() => setSelectedContact(c)}
                  className="flex cursor-pointer items-center gap-1.5 text-xs font-bold text-primary transition hover:text-[#a01820]"
                >
                  <img src={info} className="w-4 h-4" alt="info" />
                  VEDI DETTAGLI
                </button>

                <div className="flex items-center gap-3">
                  <img
                    src={c.preferito ? cuorePieno : cuoreVuoto}
                    className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                    onClick={() => togglePreferito(c.id, !!c.preferito, c.tipo as TipoContatto)}
                    alt="preferito"
                  />
                  {user?.role === 'admin' && (
                    deleteConfirmId === c.id ? (
                      <button onClick={() => eliminaContatto(c.id, c.tipo as TipoContatto)} className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200 hover:bg-red-100 transition">OK</button>
                    ) : (
                      <img src={cestino} className="w-5 h-5 cursor-pointer opacity-40 hover:opacity-100 transition hover:scale-110" onClick={() => setDeleteConfirmId(c.id)} alt="elimina" />
                    )
                  )}
                </div>
              </div>
            </LeadCard>
          );
        })}
      </div>
      {selectedContact && (
        <DettagliContattoModal
          contatto={selectedContact}
          onClose={() => setSelectedContact(null)}
          onSave={handleContactSaved}
        />
      )}
    </div>
  );
}